import type { ColorValue } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import "../theme";
import type { AppColorPalette, AppColorPaletteShades } from "../theme";

/**
 * A theme colour name for a surface: a semantic colour (`"muted"`,
 * `"surfaceCard"`) or a palette (`"blue"`), which resolves to a light shade in
 * light mode and a dark shade in dark mode.
 */
export type ViewColorPalette = AppColorPalette | AppColorPaletteShades;

// Type predicate instead of an `as` cast.
function isStringRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null;
}

/** Resolves a `ViewColorPalette` name to a colour in the active theme. */
export function useViewColor(): (colorProp: ViewColorPalette | undefined) => ColorValue | undefined {
  const { theme } = useUnistyles();
  const isDarkMode = theme.currentTheme === "dark";

  return (colorProp: ViewColorPalette | undefined): ColorValue | undefined => {
    if (colorProp === undefined) return undefined;

    const color: unknown = theme.colors[colorProp];

    if (typeof color === "string") return color;

    if (isStringRecord(color)) {
      const shade = isDarkMode ? "800" : "200";
      const resolved: unknown = color[shade];
      if (typeof resolved === "string") return resolved;
    }

    return undefined;
  };
}
